import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';

import {
  CaretDown,
  CaretUp,
  CheckCircle,
  CircleNotch,
  Clock,
  Lightning,
  PaperPlaneRight,
  Robot,
  Sparkle,
  User,
  WarningCircle,
  X,
  XCircle
} from '@phosphor-icons/react';
/**
 * OperatorChatModal - Member-facing chat interface for Operator agents
 * 
 * This provides a web-based alternative to Telegram/WhatsApp for
 * interacting with assigned Operator agents.
 * 
 * Features:
 * - Real-time chat with AI
 * - Proactive suggestions with approve/deny
 * - Command execution status
 * - Conversation history
 */
const OperatorChatModal = ({ 
  isOpen, 
  onClose, 
  operator,
  showSuggestions = true 
}) => {
  const { activeOrganization } = useOrganization();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      loadConversationHistory();
      if (showSuggestions) {
        loadSuggestions();
      }
    }
  }, [isOpen, operator?.id]);

  const loadConversationHistory = async () => {
    if (!operator?.id || !activeOrganization?.organization_id) return;

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/agents/${operator.id}/chat/history`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setSessionId(data.session_id);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const loadSuggestions = async () => {
    if (!operator?.id || !activeOrganization?.organization_id) return;

    setLoadingSuggestions(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/suggestions/pending?agent_id=${operator.id}`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    setLoading(true);

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/agents/${operator.id}/chat/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: userMessage,
            session_id: sessionId
          })
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        
        // Add assistant response
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString(),
          tool_calls: data.tool_calls || []
        }]);

        if (data.session_id) {
          setSessionId(data.session_id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
        
        // Add error message
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date().toISOString(),
          isError: true
        }]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionAction = async (suggestionId, action) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/suggestions/${suggestionId}/${action}`,
        { method: 'POST' },
        activeOrganization.organization_id
      );

      if (response.ok) {
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        toast.success(`Suggestion ${action === 'approve' ? 'approved' : 'dismissed'}`);
      }
    } catch (error) {
      console.error('Failed to handle suggestion:', error);
      toast.error('Failed to process suggestion');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center"
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-2xl h-[80vh] bg-[#1F2937] rounded-2xl border border-[rgba(255,255,255,0.08)] shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.08)]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[rgba(0,229,192,0.12)] flex items-center justify-center">
                <Robot size={20} className="text-[#00E5C0]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#F0F4F8]">{operator?.name || 'Operator'}</h2>
                <p className="text-xs text-[#7D8896]">
                  {operator?.status === 'active' ? (
                    <span className="flex items-center gap-1 text-[#00E5C0]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00E5C0]"></span>
                      Online
                    </span>
                  ) : (
                    <span className="text-[#7D8896]">Offline</span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[rgba(255,255,255,0.06)] transition-colors"
            >
              <X size={20} className="text-[#7D8896]" />
            </button>
          </div>

          {/* Suggestions Panel (Collapsible) */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="border-b border-[rgba(255,255,255,0.08)]">
              <button
                onClick={() => setShowSuggestionsPanel(!showSuggestionsPanel)}
                className="w-full px-6 py-2 flex items-center justify-between text-sm text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.03)] transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Lightning size={14} className="text-[#F59E0B]" />
                  {suggestions.length} Pending Suggestion{suggestions.length !== 1 && 's'}
                </span>
                {showSuggestionsPanel ? <CaretUp size={16} /> : <CaretDown size={16} />}
              </button>
              
              <AnimatePresence>
                {showSuggestionsPanel && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-4 space-y-2 max-h-40 overflow-y-auto">
                      {suggestions.map(suggestion => (
                        <div
                          key={suggestion.id}
                          className="p-3 rounded-lg bg-[rgba(245,158,11,0.06)] border border-[rgba(245,158,11,0.15)]"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <p className="text-sm text-[#F0F4F8] font-medium">{suggestion.title}</p>
                              <p className="text-xs text-[#7D8896] mt-1 line-clamp-2">{suggestion.description}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                onClick={() => handleSuggestionAction(suggestion.id, 'approve')}
                                className="p-1.5 rounded-lg bg-[rgba(0,229,192,0.12)] text-[#00E5C0] hover:bg-[rgba(0,229,192,0.2)] transition-colors"
                                title="Approve"
                              >
                                <CheckCircle size={16} />
                              </button>
                              <button
                                onClick={() => handleSuggestionAction(suggestion.id, 'reject')}
                                className="p-1.5 rounded-lg bg-[rgba(255,107,107,0.12)] text-[#FF6B6B] hover:bg-[rgba(255,107,107,0.2)] transition-colors"
                                title="Dismiss"
                              >
                                <XCircle size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#111827] border border-[rgba(255,255,255,0.08)] flex items-center justify-center mb-4">
                  <Sparkle size={28} className="text-[#00E5C0]" />
                </div>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">Start a conversation</h3>
                <p className="text-sm text-[#7D8896] max-w-xs">
                  Ask me to check files, run commands, browse the web, or help with tasks on your computer.
                </p>
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-lg bg-[rgba(0,229,192,0.12)] flex items-center justify-center flex-shrink-0">
                      <Robot size={16} className="text-[#00E5C0]" />
                    </div>
                  )}
                  
                  <div className={`max-w-[80%] ${message.role === 'user' ? 'order-first' : ''}`}>
                    <div
                      className={`px-4 py-2.5 rounded-2xl ${
                        message.role === 'user'
                          ? 'bg-[#00E5C0] text-[#12161C] rounded-tr-sm'
                          : message.isError
                            ? 'bg-[rgba(255,107,107,0.08)] border border-[rgba(255,107,107,0.15)] text-[#FF6B6B] rounded-tl-sm'
                            : 'bg-[#111827] border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] rounded-tl-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      
                      {/* Tool calls indicator */}
                      {message.tool_calls?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-[rgba(255,255,255,0.08)]">
                          <p className="text-xs text-[#B0B8C4] flex items-center gap-1">
                            <Lightning size={10} />
                            Executed {message.tool_calls.length} action{message.tool_calls.length !== 1 && 's'}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-[10px] text-[#5C6778] mt-1 px-2">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                  
                  {message.role === 'user' && (
                    <div className="w-8 h-8 rounded-lg bg-[#1A2030] flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-[#B0B8C4]" />
                    </div>
                  )}
                </div>
              ))
            )}
            
            {/* Loading indicator */}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-[rgba(0,229,192,0.12)] flex items-center justify-center flex-shrink-0">
                  <Robot size={16} className="text-[#00E5C0]" />
                </div>
                <div className="px-4 py-2.5 rounded-2xl bg-[#111827] border border-[rgba(255,255,255,0.08)] rounded-tl-sm">
                  <div className="flex items-center gap-2">
                    <CircleNotch size={14} className="animate-spin text-[#00E5C0]" />
                    <span className="text-sm text-[#7D8896]">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="px-6 py-4 border-t border-[rgba(255,255,255,0.08)]">
            <div className="flex items-end gap-3">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask me anything..."
                  rows={1}
                  className="w-full px-4 py-3 rounded-xl bg-[#111827] border border-[rgba(255,255,255,0.08)] focus:border-[rgba(0,229,192,0.4)] focus:outline-none text-[#F0F4F8] placeholder:text-[#5C6778] resize-none text-sm transition-colors"
                  style={{ minHeight: '48px', maxHeight: '120px' }}
                  disabled={loading}
                />
              </div>
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="p-3 rounded-xl bg-[#00E5C0] text-[#12161C] hover:bg-[#00CCB0] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <CircleNotch size={20} className="animate-spin" />
                ) : (
                  <PaperPlaneRight size={20} />
                )}
              </button>
            </div>
            
            <p className="text-[10px] text-[#5C6778] mt-2 text-center">
              Press Enter to send · Shift+Enter for new line
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OperatorChatModal;
